/**
 * THE STANDING REACTION (v8.325.0) - what a combatant does when shot at,
 * unless somebody says otherwise.
 *
 * A defender used to pay two to four clicks per reaction, and the GM paid
 * them for every ork. A standing choice on the combatant answers the
 * prompt's first question by itself after a short countdown, and any
 * click still overrides it - the table adjudicates, the sim only offers.
 * Consistent with the expiry ruling (2026-08-17): a defender who says
 * nothing gets hit; now they dodge instead, if that is what they stand for.
 *
 * `null` means ASK, which is the default for everyone (the user's ruling).
 * Pure, zero React dependency.
 */
export type StandingReaction = 'dodge' | 'parryElseDodge' | 'none';

export const STANDING_REACTIONS: { value: StandingReaction | ''; label: string }[] = [
  { value: '', label: 'Ask me' },
  { value: 'dodge', label: 'Always dodge' },
  { value: 'parryElseDodge', label: 'Parry a melee blow, else dodge' },
  { value: 'none', label: 'Never react' },
];

export interface StandingContext {
  canDodge: boolean;
  canParry: boolean;
  isMelee: boolean;
  canParryRanged?: boolean;
}

/**
 * The prompt's first answer, or null to ask. A standing choice the
 * combatant cannot honour right now (a stunned dodger) falls through to
 * the next thing they can do, and finally to taking the hits - which is
 * what the prompt's expiry would have decided anyway.
 */
export function standingChoice(
  standing: StandingReaction | null | undefined,
  ctx: StandingContext,
): 'dodge' | 'parry' | 'none' | null {
  if (!standing) return null;
  if (standing === 'none') return 'none';
  const parryable = ctx.canParry && (ctx.isMelee || !!ctx.canParryRanged);
  if (standing === 'parryElseDodge' && parryable) return 'parry';
  if (ctx.canDodge) return 'dodge';
  return 'none';
}

/** Seconds the prompt counts down before a standing choice lands. */
export const STANDING_COUNTDOWN_S = 3;
